import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import {
  ChevronLeft, ChevronRight, TrendingUp, Users, FileText,
  Package, Trophy, ArrowRight, Zap, Star
} from 'lucide-react';

// ─── Config ───────────────────────────────────────────────────────────────────
const API = 'https://apisaleeasy.psisat.com';
const DATE_FROM = '2025-12-01';
const DATE_TO = (() => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
})();
const TOTAL_SLIDES = 6;

// ─── Formatters ───────────────────────────────────────────────────────────────
const fmt = (n) =>
  Number(n || 0).toLocaleString('th-TH', { maximumFractionDigits: 0 });

const fmtM = (n) =>
  (Number(n || 0) / 1e6).toLocaleString('th-TH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtK = (n) =>
  (Number(n || 0) / 1e3).toLocaleString('th-TH', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

// ─── Colors ───────────────────────────────────────────────────────────────────
const TYPE_COLORS = {
  SAT:   '#f59e0b',
  Solar: '#10b981',
  MTF:   '#3b82f6',
  MTB:   '#06b6d4',
  Other: '#64748b',
};

const RANK_COLORS = ['#f59e0b', '#94a3b8', '#b45309', '#3b82f6', '#10b981'];

// ─── Shared slide wrapper ─────────────────────────────────────────────────────
const SlideWrapper = ({ children, gradient = 'from-slate-950 via-slate-900 to-slate-950' }) => (
  <div
    className={`w-full h-full flex flex-col justify-center px-20 bg-gradient-to-br ${gradient}`}
  >
    {children}
  </div>
);

// ─── PSI Logo (reusable) ─────────────────────────────────────────────────────
const PsiLogo = ({ size = 48, className = '' }) => (
  <img
    src="/logo-psi2018.png"
    alt="PSI"
    style={{ height: size, width: 'auto', objectFit: 'contain' }}
    className={className}
  />
);

// ─── Persistent Footer (logo + company name) shown on all slides ─────────────
const SlideFooter = () => (
  <div className="fixed bottom-16 left-8 flex items-center gap-3 z-40 opacity-40 hover:opacity-70 transition-opacity">
    <PsiLogo size={28} />
    <span className="text-slate-500 text-xs tracking-widest uppercase font-medium">
      PSI Smart Sale Report
    </span>
  </div>
);

// ─── Slide 0: Cover ──────────────────────────────────────────────────────────
const Slide0_Cover = () => (
  <div
    className="w-full h-full flex flex-col items-center justify-center relative overflow-hidden"
    style={{
      background:
        'radial-gradient(ellipse 80% 60% at 50% 40%, #1e293b 0%, #0a0f1a 60%, #000 100%)',
    }}
  >
    {/* Decorative rings */}
    {[700, 520, 360].map((size, i) => (
      <div
        key={i}
        className="absolute rounded-full border border-slate-700/20 pointer-events-none"
        style={{ width: size, height: size }}
      />
    ))}

    {/* Glowing dot grid */}
    <div
      className="absolute inset-0 opacity-10 pointer-events-none"
      style={{
        backgroundImage:
          'radial-gradient(circle, #f59e0b 1px, transparent 1px)',
        backgroundSize: '40px 40px',
      }}
    />

    <div className="relative z-10 text-center max-w-4xl">
      {/* PSI Logo */}
      <div className="flex items-center justify-center mb-10">
        <div className="bg-white/5 border border-white/10 rounded-2xl px-8 py-5 backdrop-blur-sm">
          <PsiLogo size={72} />
        </div>
      </div>

      <h1 className="text-7xl font-bold text-white mb-5 leading-tight tracking-tight">
        สรุปผลการดำเนินงาน
      </h1>
      <h2 className="text-4xl font-light text-slate-300 mb-10">
        ระบบ Sale-Easy
      </h2>

      <div className="flex items-center justify-center gap-4 text-2xl text-slate-400">
        <span className="font-medium text-slate-300">ธ.ค. 2568</span>
        <ArrowRight size={22} className="text-amber-400" />
        <span className="font-medium text-slate-300">มิ.ย. 2569</span>
      </div>

      <p className="mt-16 text-slate-600 text-sm tracking-widest uppercase">
        กด Space หรือ → เพื่อดำเนินการต่อ
      </p>
    </div>
  </div>
);

// ─── Slide 1: KPI Overview ───────────────────────────────────────────────────
const Slide1_Overview = ({ data }) => {
  const totalAmount = data.totalAmount || 0;
  const totalBills = data.totalBills || 0;
  const totalCustomers = data.customers?.length || 0;

  const kpis = [
    {
      label: 'ยอดขายรวมทั้งสิ้น',
      value: fmtM(totalAmount),
      unit: 'ล้านบาท',
      icon: <TrendingUp size={22} className="text-amber-400" />,
      bg: 'from-amber-500/10 to-amber-600/5',
      border: 'border-amber-400/25',
      color: 'text-amber-400',
    },
    {
      label: 'จำนวนใบแจ้งหนี้',
      value: fmt(totalBills),
      unit: 'รายการ',
      icon: <FileText size={22} className="text-blue-400" />,
      bg: 'from-blue-500/10 to-blue-600/5',
      border: 'border-blue-400/25',
      color: 'text-blue-400',
    },
    {
      label: 'ลูกค้าที่ซื้อในระบบ',
      value: fmt(totalCustomers),
      unit: 'ราย',
      icon: <Users size={22} className="text-emerald-400" />,
      bg: 'from-emerald-500/10 to-emerald-600/5',
      border: 'border-emerald-400/25',
      color: 'text-emerald-400',
    },
  ];

  return (
    <SlideWrapper>
      <div className="mb-12">
        <p className="text-amber-400 text-xs font-semibold tracking-[0.2em] uppercase mb-3">
          ภาพรวม 7 เดือน
        </p>
        <h2 className="text-6xl font-bold text-white leading-tight">
          ยอดขายรวม
        </h2>
        <p className="text-slate-500 mt-3 text-lg">
          1 ธันวาคม 2568 – ปัจจุบัน (บริษัทในกลุ่ม PSI ทุกสาขา)
        </p>
      </div>

      <div className="grid grid-cols-3 gap-7">
        {kpis.map((k, i) => (
          <div
            key={i}
            className={`bg-gradient-to-br ${k.bg} border ${k.border} rounded-2xl p-8`}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                {k.icon}
              </div>
              <span className="text-slate-400 text-sm">{k.label}</span>
            </div>
            <div className="text-5xl font-bold text-white mb-2 tabular-nums">
              {k.value}
            </div>
            <div className={`${k.color} text-lg font-medium`}>{k.unit}</div>
          </div>
        ))}
      </div>

      <div className="mt-10 flex items-center gap-4 text-slate-600 text-sm">
        <span>ข้อมูล ณ {DATE_TO}</span>
        <span>•</span>
        <span>บริษัท 045 / 043 / 012</span>
      </div>
    </SlideWrapper>
  );
};

// ─── Slide 2: Top 10 Products ─────────────────────────────────────────────────
const CustomBarTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 shadow-2xl max-w-xs">
      <p className="text-white text-sm font-semibold mb-1 leading-snug">{d.fullDesc}</p>
      <p className="text-slate-400 text-xs font-mono mb-3">{d.code}</p>
      <div className="flex gap-6">
        <div>
          <p className="text-slate-500 text-xs mb-0.5">ยอดขาย</p>
          <p className="text-amber-400 font-bold text-sm">{fmt(d.amount * 1000)} บาท</p>
        </div>
        <div>
          <p className="text-slate-500 text-xs mb-0.5">จำนวน</p>
          <p className="text-blue-400 font-bold text-sm">{fmt(d.qty)} ชิ้น</p>
        </div>
      </div>
    </div>
  );
};

// Custom right-side label showing amount + qty on two lines
const ProductBarLabel = ({ x, y, width, value, index, chartData }) => {
  const item = chartData[index];
  if (!item) return null;
  const lx = x + width + 8;
  const ly = y + 9;
  return (
    <g>
      <text x={lx} y={ly} fill="#94a3b8" fontSize={11} fontWeight="600">
        {fmt(value)}K
      </text>
      <text x={lx} y={ly + 14} fill="#475569" fontSize={10}>
        {fmt(item.qty)} ชิ้น
      </text>
    </g>
  );
};

const Slide2_Products = ({ data }) => {
  const products = (data.products || []).slice(0, 10);
  const chartData = products.map((p, i) => {
    const desc = (p.product_description || p.product_code || '').trim();
    const code = (p.product_code || '').trim();
    return {
      name: desc.length > 28 ? desc.slice(0, 28) + '…' : desc,
      fullDesc: desc,
      code,
      amount: Math.round((p.net_amount || 0) / 1000),
      qty: parseInt(p.total_quantity || 0),
      rank: i + 1,
    };
  });

  return (
    <SlideWrapper>
      <div className="mb-6">
        <p className="text-blue-400 text-xs font-semibold tracking-[0.2em] uppercase mb-3">
          สินค้า
        </p>
        <h2 className="text-6xl font-bold text-white leading-tight">
          Top 10 สินค้าขายดี
        </h2>
        <p className="text-slate-500 mt-2 text-lg">ยอดขาย (พันบาท) · จำนวนชิ้น · ธ.ค. 2568 – ปัจจุบัน</p>
      </div>

      <div style={{ height: 410 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ left: 0, right: 100, top: 0, bottom: 0 }}
          >
            <XAxis
              type="number"
              tick={{ fill: '#475569', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `${fmt(v)}K`}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fill: '#94a3b8', fontSize: 11 }}
              width={240}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomBarTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
            <Bar
              dataKey="amount"
              radius={[0, 6, 6, 0]}
              label={(props) => <ProductBarLabel {...props} chartData={chartData} />}
            >
              {chartData.map((_, i) => (
                <Cell
                  key={i}
                  fill={`hsl(${210 + i * 12}, 75%, ${62 - i * 4}%)`}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </SlideWrapper>
  );
};

// ─── Slide 3: Product Type Breakdown ─────────────────────────────────────────
const Slide3_ProductTypes = ({ data }) => {
  const typeTotals = data.productTypeTotals || {};
  const typeData = Object.entries(typeTotals)
    .map(([type, amount]) => ({
      name: type,
      amount: Number(amount || 0),
      amountM: Math.round((Number(amount || 0) / 1e6) * 100) / 100,
    }))
    .filter((t) => t.amount > 0)
    .sort((a, b) => b.amount - a.amount);

  const totalType = typeData.reduce((s, t) => s + t.amount, 0);

  return (
    <SlideWrapper>
      <div className="mb-10">
        <p className="text-purple-400 text-xs font-semibold tracking-[0.2em] uppercase mb-3">
          หมวดหมู่สินค้า
        </p>
        <h2 className="text-6xl font-bold text-white leading-tight">
          ยอดขายแบ่งตามประเภท
        </h2>
        <p className="text-slate-500 mt-3 text-lg">
          สัดส่วนยอดขายแต่ละกลุ่มสินค้า
        </p>
      </div>

      <div className="grid grid-cols-2 gap-16 items-center">
        {/* Chart */}
        <div style={{ height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={typeData} margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
              <XAxis
                dataKey="name"
                tick={{ fill: '#94a3b8', fontSize: 13 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: '#475569', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `${fmtK(v)}K`}
              />
              <Tooltip
                contentStyle={{
                  background: '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: '10px',
                  color: '#f1f5f9',
                }}
                formatter={(v) => [`${fmtM(v)} ล้านบาท`, 'ยอดขาย']}
                cursor={{ fill: 'rgba(255,255,255,0.03)' }}
              />
              <Bar dataKey="amount" radius={[6, 6, 0, 0]}>
                {typeData.map((entry) => (
                  <Cell
                    key={entry.name}
                    fill={TYPE_COLORS[entry.name] || '#64748b'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Legend + progress bars */}
        <div className="space-y-5">
          {typeData.map((t) => {
            const pct =
              totalType > 0 ? Math.round((t.amount / totalType) * 100) : 0;
            const color = TYPE_COLORS[t.name] || '#64748b';
            return (
              <div key={t.name}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ background: color }}
                    />
                    <span className="text-slate-200 font-medium">{t.name}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-white font-bold">{t.amountM}</span>
                    <span className="text-slate-500 text-sm ml-1">ล้าน</span>
                    <span
                      className="text-xs ml-2 font-medium"
                      style={{ color }}
                    >
                      {pct}%
                    </span>
                  </div>
                </div>
                <div className="w-full bg-white/5 rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all duration-700"
                    style={{ width: `${pct}%`, background: color }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </SlideWrapper>
  );
};

// ─── Slide 4: Customer Insights ───────────────────────────────────────────────
const CustomerRow = ({ c, i, rightContent }) => (
  <div className="flex items-center gap-2.5 py-1.5 border-b border-white/5 last:border-0">
    <span
      className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
      style={{
        background: i === 0 ? '#f59e0b' : i === 1 ? '#94a3b8' : i === 2 ? '#b45309' : 'rgba(255,255,255,0.08)',
        color: i < 3 ? '#0a0f1a' : '#64748b',
      }}
    >
      {i + 1}
    </span>
    <div className="flex-1 min-w-0">
      <p className="text-white text-xs font-medium truncate leading-tight">
        {c.customer_name || '—'}
      </p>
      <p className="text-slate-600 text-xs font-mono leading-tight">{c.customer_code}</p>
    </div>
    {rightContent(c)}
  </div>
);

const Slide4_Customers = ({ data }) => {
  const totalRanking = data.allRankingCount || 0;
  const repeatCount  = data.repeatCount || 0;
  const onceOnly     = totalRanking - repeatCount;
  const repeatRate   = totalRanking > 0 ? Math.round((repeatCount / totalRanking) * 100) : 0;

  const top10ByAmount = data.top10ByAmount || [];
  const top10ByRepeat = data.top10ByRepeat || [];

  return (
    <SlideWrapper>
      <div className="mb-5">
        <p className="text-emerald-400 text-xs font-semibold tracking-[0.2em] uppercase mb-2">
          ลูกค้า
        </p>
        <h2 className="text-5xl font-bold text-white leading-tight">Customer Insights</h2>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
          <p className="text-slate-500 text-xs mb-1">ลูกค้าทั้งหมด</p>
          <p className="text-3xl font-bold text-white tabular-nums">{fmt(totalRanking)}</p>
          <p className="text-slate-600 text-xs mt-0.5">ราย</p>
        </div>
        <div className="bg-emerald-500/10 border border-emerald-400/20 rounded-xl p-4 text-center">
          <p className="text-slate-500 text-xs mb-1">ซื้อซ้ำ (Repeat)</p>
          <p className="text-3xl font-bold text-emerald-400 tabular-nums">{fmt(repeatCount)}</p>
          <p className="text-slate-600 text-xs mt-0.5">ราย · {repeatRate}% ของทั้งหมด</p>
        </div>
        <div className="bg-slate-800/60 border border-white/8 rounded-xl p-4 text-center">
          <p className="text-slate-500 text-xs mb-1">ซื้อครั้งเดียว</p>
          <p className="text-3xl font-bold text-slate-300 tabular-nums">{fmt(onceOnly)}</p>
          <p className="text-slate-600 text-xs mt-0.5">ราย</p>
        </div>
      </div>

      {/* Repeat bar */}
      <div className="flex rounded-full overflow-hidden h-3 mb-6">
        <div style={{ width: `${repeatRate}%`, background: '#10b981' }} />
        <div style={{ width: `${100 - repeatRate}%`, background: 'rgba(255,255,255,0.05)' }} />
      </div>

      {/* Two lists */}
      <div className="grid grid-cols-2 gap-8">
        {/* Top 10 by amount */}
        <div>
          <p className="text-amber-400 text-xs font-semibold uppercase tracking-widest mb-3">
            Top 10 · ยอดซื้อสูงสุด
          </p>
          <div>
            {top10ByAmount.map((c, i) => (
              <CustomerRow
                key={c.customer_code}
                c={c}
                i={i}
                rightContent={(c) => (
                  <span className="text-amber-400 font-bold text-xs flex-shrink-0">
                    {fmtM(c.total_amount)}M
                  </span>
                )}
              />
            ))}
          </div>
        </div>

        {/* Top 10 by repeat */}
        <div>
          <p className="text-emerald-400 text-xs font-semibold uppercase tracking-widest mb-3">
            Top 10 · ซื้อซ้ำสูงสุด (จำนวนใบ)
          </p>
          <div>
            {top10ByRepeat.length > 0 ? top10ByRepeat.map((c, i) => (
              <CustomerRow
                key={c.customer_code}
                c={c}
                i={i}
                rightContent={(c) => (
                  <span className="text-emerald-400 font-bold text-xs flex-shrink-0">
                    {fmt(c.invoice_count)} ใบ
                  </span>
                )}
              />
            )) : (
              <p className="text-slate-600 text-sm">ไม่มีข้อมูลการซื้อซ้ำ</p>
            )}
          </div>
        </div>
      </div>
    </SlideWrapper>
  );
};

// ─── Slide 5: Closing ─────────────────────────────────────────────────────────
const Slide5_Closing = ({ data }) => {
  const totalAmount    = data.totalAmount || 0;
  const totalBills     = data.totalBills || 0;
  const totalRanking   = data.allRankingCount || 0;

  const highlights = [
    {
      icon: <TrendingUp size={26} className="text-amber-400" />,
      label: 'ยอดขายรวม',
      value: `${fmtM(totalAmount)} ล้านบาท`,
      border: 'border-amber-400/30',
    },
    {
      icon: <FileText size={26} className="text-blue-400" />,
      label: 'ใบแจ้งหนี้',
      value: `${fmt(totalBills)} รายการ`,
      border: 'border-blue-400/30',
    },
    {
      icon: <Users size={26} className="text-emerald-400" />,
      label: 'ลูกค้าในระบบ',
      value: `${fmt(totalRanking)} ราย`,
      border: 'border-emerald-400/30',
    },
  ];

  return (
    <div
      className="w-full h-full flex flex-col items-center justify-center relative overflow-hidden"
      style={{
        background:
          'radial-gradient(ellipse 80% 60% at 50% 40%, #1e293b 0%, #0a0f1a 60%, #000 100%)',
      }}
    >
      <div
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, #f59e0b 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      <div className="relative z-10 text-center max-w-4xl w-full px-12">
        {/* PSI Logo */}
        <div className="flex items-center justify-center mb-8">
          <div className="bg-white/5 border border-white/10 rounded-2xl px-10 py-6 backdrop-blur-sm">
            <PsiLogo size={80} />
          </div>
        </div>

        <h2 className="text-5xl font-bold text-white mb-3">PSI Smart Sale</h2>
        <p className="text-slate-400 text-xl mb-14">
          ข้อมูลจริง · อัปเดต real-time · พร้อมใช้งานทุกวัน
        </p>

        <div className="grid grid-cols-3 gap-5">
          {highlights.map((h, i) => (
            <div
              key={i}
              className={`border ${h.border} bg-white/3 rounded-2xl p-6 text-center`}
            >
              <div className="flex justify-center mb-3">{h.icon}</div>
              <p className="text-white font-bold text-xl">{h.value}</p>
              <p className="text-slate-500 text-sm mt-1">{h.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Loading screen ───────────────────────────────────────────────────────────
const LoadingScreen = () => (
  <div
    className="fixed inset-0 flex flex-col items-center justify-center"
    style={{ background: '#0a0f1a' }}
  >
    <div className="relative mb-8">
      <div className="w-16 h-16 rounded-full border-2 border-amber-400/20 animate-ping absolute inset-0" />
      <div className="w-16 h-16 rounded-full border-2 border-t-amber-400 border-r-transparent border-b-transparent border-l-transparent animate-spin" />
    </div>
    <p className="text-slate-400 text-base tracking-widest uppercase">
      กำลังโหลดข้อมูล...
    </p>
    <p className="text-slate-700 text-sm mt-2">
      {DATE_FROM} → {DATE_TO}
    </p>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
export default function PresentationDashboardComponent() {
  const [slide, setSlide] = useState(0);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  const next = useCallback(
    () => setSlide((s) => Math.min(s + 1, TOTAL_SLIDES - 1)),
    []
  );
  const prev = useCallback(() => setSlide((s) => Math.max(s - 1, 0)), []);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (
        e.key === 'ArrowRight' ||
        e.key === 'ArrowDown' ||
        e.key === ' '
      )
        next();
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') prev();
      if (e.key >= '1' && e.key <= '6') setSlide(Number(e.key) - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev]);

  // Fetch all data in parallel on mount
  useEffect(() => {
    (async () => {
      try {
        const [salesRes, prodRes, custRes, rankRes] = await Promise.allSettled([
          fetch(`${API}/api/sales-team/combined-sales-summary`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              date_range: { start_date: DATE_FROM, end_date: DATE_TO },
            }),
          }).then((r) => r.json()),

          fetch(
            `${API}/api/analytics/product-sales/top-products?dateFrom=${DATE_FROM}&dateTo=${DATE_TO}&limit=200`
          ).then((r) => r.json()),

          fetch(`${API}/api/sales-analytics/customer-summary`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              date_range: {
                start_date: DATE_FROM,
                end_date: DATE_TO,
                group_by: 'month',
              },
              filters: { company_code: '045' },
            }),
          }).then((r) => r.json()),

          fetch(`${API}/api/sales-analytics/customer-ranking-cached`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              date_range: { start_date: DATE_FROM, end_date: DATE_TO },
              filters: { company_code: '045' },
            }),
          }).then((r) => r.json()),
        ]);

        const sales =
          salesRes.status === 'fulfilled' ? salesRes.value : null;
        const prods =
          prodRes.status === 'fulfilled' ? prodRes.value : null;
        const cust =
          custRes.status === 'fulfilled' ? custRes.value : null;
        const rank =
          rankRes.status === 'fulfilled' ? rankRes.value : null;

        // All customers from ranking API (has invoice_count)
        const allRanking = rank?.data?.customers || [];

        // Top 10 by total_amount
        const top10Amount = [...allRanking]
          .sort((a, b) => (b.total_amount || 0) - (a.total_amount || 0))
          .slice(0, 10);

        // Top 10 by invoice_count (repeat buyers)
        const top10Repeat = [...allRanking]
          .filter((c) => (c.invoice_count || 0) > 1)
          .sort((a, b) => (b.invoice_count || 0) - (a.invoice_count || 0))
          .slice(0, 10);

        // Fetch names for unique customer codes across both lists in parallel
        const uniqueCodes = [
          ...new Set([...top10Amount, ...top10Repeat].map((c) => c.customer_code)),
        ];
        const nameResults = await Promise.allSettled(
          uniqueCodes.map((code) =>
            fetch(`${API}/api/technician/by-code`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ code: String(code).trim() }),
            }).then((r) => r.json())
          )
        );
        const nameMap = {};
        uniqueCodes.forEach((code, i) => {
          const res =
            nameResults[i].status === 'fulfilled' ? nameResults[i].value : null;
          if (res?.status && res?.data) {
            const first =
              res.data.technician_name || res.data.name ||
              res.data.customer_name || res.data.first_name || '';
            const last =
              res.data.technician_surname || res.data.surname ||
              res.data.last_name || '';
            nameMap[code] = `${first}${last ? ' ' + last : ''}`.trim() || null;
          }
        });
        const attachName = (c) => ({ ...c, customer_name: nameMap[c.customer_code] || null });

        // Repeat stats from ranking data (reliable invoice_count)
        const repeatCount = allRanking.filter((c) => (c.invoice_count || 0) > 1).length;

        setData({
          totalAmount: sales?.data?.total_amount || 0,
          totalBills: sales?.data?.total_bills || 0,
          products: (prods?.data || [])
            .sort((a, b) => (b.net_amount || 0) - (a.net_amount || 0))
            .slice(0, 10),
          customers: cust?.data?.customers || [],
          allRankingCount: allRanking.length,
          repeatCount,
          top10ByAmount: top10Amount.map(attachName),
          top10ByRepeat: top10Repeat.map(attachName),
          topCustomers: top10Amount.slice(0, 5).map(attachName), // keep for closing slide
          productTypeTotals:
            rank?.data?.summary?.product_type_totals || {},
        });
      } catch (err) {
        console.error('Presentation fetch error:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <LoadingScreen />;

  const slides = [
    <Slide0_Cover />,
    <Slide1_Overview data={data} />,
    <Slide2_Products data={data} />,
    <Slide3_ProductTypes data={data} />,
    <Slide4_Customers data={data} />,
    <Slide5_Closing data={data} />,
  ];

  const SLIDE_LABELS = [
    'Cover',
    'ภาพรวม',
    'สินค้า',
    'หมวดหมู่',
    'ลูกค้า',
    'สรุป',
  ];

  return (
    <div
      className="fixed inset-0 overflow-hidden"
      style={{ background: '#0a0f1a', fontFamily: "'Noto Sans Thai', sans-serif" }}
    >
      {/* Slide */}
      <div className="w-full h-full">{slides[slide]}</div>

      {/* Persistent PSI logo footer */}
      <SlideFooter />

      {/* Slide counter (top-right) */}
      <div className="fixed top-5 right-7 flex items-center gap-2 text-slate-600 text-sm font-mono z-50">
        <span className="text-slate-400">{slide + 1}</span>
        <span>/</span>
        <span>{TOTAL_SLIDES}</span>
      </div>

      {/* Slide label (top-left) */}
      <div className="fixed top-5 left-7 text-slate-700 text-xs tracking-widest uppercase z-50">
        {SLIDE_LABELS[slide]}
      </div>

      {/* Bottom navigation */}
      <div className="fixed bottom-6 left-0 right-0 flex items-center justify-center gap-5 z-50">
        <button
          onClick={prev}
          disabled={slide === 0}
          className="p-2.5 rounded-full bg-white/8 hover:bg-white/15 text-slate-400 hover:text-white disabled:opacity-20 transition-all"
        >
          <ChevronLeft size={20} />
        </button>

        {/* Dot indicators */}
        <div className="flex items-center gap-2">
          {Array.from({ length: TOTAL_SLIDES }, (_, i) => (
            <button
              key={i}
              onClick={() => setSlide(i)}
              title={SLIDE_LABELS[i]}
              className={`rounded-full transition-all duration-300 ${
                i === slide
                  ? 'w-6 h-2 bg-amber-400'
                  : 'w-2 h-2 bg-white/20 hover:bg-white/40'
              }`}
            />
          ))}
        </div>

        <button
          onClick={next}
          disabled={slide === TOTAL_SLIDES - 1}
          className="p-2.5 rounded-full bg-white/8 hover:bg-white/15 text-slate-400 hover:text-white disabled:opacity-20 transition-all"
        >
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
}
